import React, { useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import useAppStore from './store/appStore';
import Sidebar from './components/Sidebar';
import MainContent from './components/MainContent';
import Header from './components/Header';
import PasswordProtection from './components/PasswordProtection';
import { fetchAgencies, fetchRegulations, fetchProvisions } from './services/api';
import { getAgencyConfig } from './config/agency';

function App() {
  const { 
    sidebarCollapsed, 
    setAgencies, 
    setRegulations,
    setAllProvisionsAnalysis,
    setLoadingAgencies,
    setLoadingRegulations,
    addError,
    setSelectedAgencies,
    selectedAgencies 
  } = useAppStore();

  // Console log on mount
  console.log('%c🚀 FERC Regulatory Analysis Platform Starting...', 'color: #2563eb; font-size: 16px; font-weight: bold');
  console.log('%c📊 Version: 1.0.1 | Environment: Production', 'color: #666; font-size: 12px');
  console.log('%c================================', 'color: #ccc');
  console.log('%c📍 URL:', 'color: #9C27B0', window.location.href);
  console.log('%c🖥️ User Agent:', 'color: #9C27B0', navigator.userAgent);
  console.log('%c⏰ Load Time:', 'color: #9C27B0', new Date().toISOString());
  console.log('%c================================', 'color: #ccc');

  useEffect(() => {
    console.log('%c⚙️ [App] Component mounted, initializing data load...', 'color: #4CAF50; font-weight: bold');
    console.log('%c🔍 [App] Current state:', 'color: #FF9800', {
      sidebarCollapsed,
      hasAgencies: false,
      hasRegulations: false
    });
    
    // Check for agency-locked mode and auto-select
    const agencyConfig = getAgencyConfig();
    if (agencyConfig.locked && agencyConfig.agency) {
      console.log('%c🔒 [App] Agency-locked mode detected, auto-selecting:', 'color: #FF9800; font-weight: bold', agencyConfig.agency);
      setSelectedAgencies([agencyConfig.agency]);
    }
    
    // Load initial data
    const loadInitialData = async () => {
      console.log('%c📡 [App] Starting data fetch sequence...', 'color: #2196F3; font-weight: bold');
      try {
        setLoadingAgencies(true);
        console.log('%c🏢 [App] Fetching agencies...', 'color: #9C27B0');
        console.time('⏱️ Agency Fetch Time');
        const agenciesData = await fetchAgencies();
        console.timeEnd('⏱️ Agency Fetch Time');
        console.log('%c✅ [App] Agencies loaded:', 'color: #4CAF50', {
          count: agenciesData.length,
          agencies: agenciesData,
          ids: agenciesData.map(a => a.id)
        });
        // Ensure it's an array
        setAgencies(Array.isArray(agenciesData) ? agenciesData : []);
      } catch (error) {
        console.error('%c❌ [App] Failed to load agencies:', 'color: #F44336; font-weight: bold', error);
        // Set default agencies if fetch fails
        setAgencies([
          { id: "dcr", name: "Department of Conservation and Recreation", acronym: "DCR" },
          { id: "vdh", name: "Virginia Department of Health", acronym: "VDH" },
          { id: "doe", name: "Department of Education", acronym: "DOE" },
          { id: "dcjs", name: "Department of Criminal Justice Services", acronym: "DCJS" },
          { id: "dbhds", name: "Department of Behavioral Health and Developmental Services", acronym: "DBHDS" },
        ]);
        addError('Using default agencies. Database connection may be unavailable.');
      } finally {
        setLoadingAgencies(false);
      }

      // Only load regulations if in agency-locked mode
      if (agencyConfig.locked && agencyConfig.agency) {
        try {
          setLoadingRegulations(true);
          console.log('%c📜 [App] Loading regulations for locked agency...', 'color: #9C27B0');
          console.time('⏱️ Regulations Fetch Time');
          const regulationsData = await fetchRegulations({ agency_ids: agencyConfig.agency });
          console.timeEnd('⏱️ Regulations Fetch Time');
          
          if (!regulationsData || !Array.isArray(regulationsData)) {
            console.error('%c❌ [App] Invalid regulations data:', 'color: #F44336', regulationsData);
            throw new Error('Invalid regulations data received');
          }
          
          console.log('%c✅ [App] Regulations loaded:', 'color: #4CAF50', {
            count: regulationsData.length,
            byAgency: regulationsData.reduce((acc, r) => {
              acc[r.agencyId] = (acc[r.agencyId] || 0) + 1;
              return acc;
            }, {}),
            sample: regulationsData.slice(0, 3)
          });
          
          // Set regulations in store
          setRegulations(regulationsData);
          console.log('%c✅ [App] Regulations set in store', 'color: #4CAF50');
          
          // Extract provisions from regulations data if they exist
          const embeddedProvisions = {};
          regulationsData.forEach(reg => {
            if (reg.provisions && reg.provisions.length > 0) {
              // Map provisions to the format expected by the frontend
              embeddedProvisions[reg.id] = reg.provisions.map(prov => ({
                ...prov,
                regulationId: reg.id,
                // Use the authorization field that exists in the data
                authLevel: prov.authorization || prov.authLevel || 'UNAUTHORIZED',
                // Mark as three-bucket since we're using FERC data with MANDATORY/AUTHORIZED/UNAUTHORIZED
                isThreeBucket: true,
                // Map is_mandatory/is_discretionary/is_unauthorized for compatibility
                is_mandatory: prov.authorization === 'MANDATORY' || prov.is_mandatory,
                is_discretionary: prov.authorization === 'AUTHORIZED' || prov.is_discretionary,  
                is_unauthorized: prov.authorization === 'UNAUTHORIZED' || prov.is_unauthorized
              }));
            }
          });
          
          if (Object.keys(embeddedProvisions).length > 0) {
            console.log('%c✅ [App] Using embedded provisions from regulations:', 'color: #4CAF50', {
              regulationCount: Object.keys(embeddedProvisions).length,
              totalProvisions: Object.values(embeddedProvisions).reduce((sum, provs) => sum + provs.length, 0)
            });
            setAllProvisionsAnalysis(embeddedProvisions);
          } else {
            // Fallback: Load provisions from separate API
            try {
              console.log('%c📝 [App] No embedded provisions, fetching from API...', 'color: #9C27B0');
              console.time('⏱️ Provisions Fetch Time');
              const provisionsData = await fetchProvisions();
              console.timeEnd('⏱️ Provisions Fetch Time');
              console.log('%c✅ [App] Provisions loaded from API:', 'color: #4CAF50', {
                totalRegulations: Object.keys(provisionsData).length,
                totalProvisions: Object.values(provisionsData).reduce((sum, p) => sum + p.length, 0),
                sample: Object.entries(provisionsData).slice(0, 3).map(([regId, provs]) => ({
                  regulationId: regId,
                  provisionCount: provs.length
                }))
              });
              setAllProvisionsAnalysis(provisionsData);
            } catch (error) {
              console.error('%c❌ [App] Failed to load provisions:', 'color: #F44336; font-weight: bold', error);
              // Provisions are optional, so we don't need to set mock data
            }
          }
        } catch (error) {
          console.error('%c❌ [App] Failed to load regulations:', 'color: #F44336; font-weight: bold', error);
          addError('Failed to load regulations for locked agency.');
        } finally {
          setLoadingRegulations(false);
        }
      } else {
        console.log('%c⏸️ [App] No agency selected, skipping regulations and provisions load', 'color: #FF9800');
      }
    };

    loadInitialData();
  }, []);

  // Load regulations when agencies are selected
  useEffect(() => {
    const agencyConfig = getAgencyConfig();
    // Skip if in locked mode (already handled above) or no agencies selected
    if (agencyConfig.locked || selectedAgencies.length === 0) {
      // Clear regulations if no agencies selected
      if (selectedAgencies.length === 0) {
        console.log('%c🧹 [App] No agencies selected, clearing regulations', 'color: #FF9800');
        setRegulations([]);
        setAllProvisionsAnalysis({});
      }
      return;
    }

    console.log('%c🔄 [App] Agencies selected, loading regulations...', 'color: #2196F3; font-weight: bold', selectedAgencies);
    
    const loadRegulationsForAgencies = async () => {
      try {
        setLoadingRegulations(true);
        console.log('%c📜 [App] Fetching regulations for selected agencies...', 'color: #9C27B0');
        const regulationsData = await fetchRegulations({ agency_ids: selectedAgencies.join(',') });
        
        if (!regulationsData || !Array.isArray(regulationsData)) {
          console.error('%c❌ [App] Invalid regulations data:', 'color: #F44336', regulationsData);
          throw new Error('Invalid regulations data received');
        }
        
        console.log('%c✅ [App] Regulations loaded:', 'color: #4CAF50', {
          count: regulationsData.length,
          forAgencies: selectedAgencies
        });
        
        setRegulations(regulationsData);
        
        // Extract provisions from regulations data if they exist
        const embeddedProvisions = {};
        regulationsData.forEach(reg => {
          if (reg.provisions && reg.provisions.length > 0) {
            // Map provisions to the format expected by the frontend
            embeddedProvisions[reg.id] = reg.provisions.map(prov => ({
              ...prov,
              regulationId: reg.id,
              // Use the authorization field that exists in the data
              authLevel: prov.authorization || prov.authLevel || 'UNAUTHORIZED',
              // Mark as three-bucket since we're using FERC data with MANDATORY/AUTHORIZED/UNAUTHORIZED
              isThreeBucket: true,
              // Map is_mandatory/is_discretionary/is_unauthorized for compatibility
              is_mandatory: prov.authorization === 'MANDATORY' || prov.is_mandatory,
              is_discretionary: prov.authorization === 'AUTHORIZED' || prov.is_discretionary,  
              is_unauthorized: prov.authorization === 'UNAUTHORIZED' || prov.is_unauthorized
            }));
          }
        });
        
        if (Object.keys(embeddedProvisions).length > 0) {
          console.log('%c✅ [App] Using embedded provisions from regulations:', 'color: #4CAF50', {
            regulationCount: Object.keys(embeddedProvisions).length,
            totalProvisions: Object.values(embeddedProvisions).reduce((sum, provs) => sum + provs.length, 0)
          });
          setAllProvisionsAnalysis(embeddedProvisions);
        } else {
          // Fallback: Also load provisions from separate API
          try {
            console.log('%c📝 [App] No embedded provisions, fetching from API...', 'color: #9C27B0');
            const provisionsData = await fetchProvisions();
            console.log('%c✅ [App] Provisions loaded from API:', 'color: #4CAF50');
            setAllProvisionsAnalysis(provisionsData);
          } catch (error) {
            console.error('%c❌ [App] Failed to load provisions:', 'color: #F44336; font-weight: bold', error);
          }
        }
      } catch (error) {
        console.error('%c❌ [App] Failed to load regulations:', 'color: #F44336; font-weight: bold', error);
        addError('Failed to load regulations. Please try again.');
      } finally {
        setLoadingRegulations(false);
      }
    };

    loadRegulationsForAgencies();
  }, [selectedAgencies]); // Re-run when selected agencies change

  return (
    <PasswordProtection>
      <div className="app-container">
        <Toaster 
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
              fontSize: '13px',
            },
          }}
        />
        
        <Header />
        
        <div className="main-layout">
          <Sidebar />
          <MainContent />
        </div>
      </div>
    </PasswordProtection>
  );
}

export default App;